import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  type ChangeEvent,
  type ChangeEventHandler,
  type ClipboardEventHandler,
  type CompositionEvent,
  type CompositionEventHandler,
  type RefObject,
} from "react";
import {
  convertAtEnd,
  convertTyped,
  ensureEmojiList,
  restoreOnBackspace,
  wordAt,
  type EmojiUndo,
} from "./convert";

/** A textarea, or a single-line input (News item captions). */
type Field = HTMLTextAreaElement | HTMLInputElement;

export interface EmojiText<T extends Field = HTMLTextAreaElement> {
  ref: RefObject<T | null>;
  props: {
    onChange: ChangeEventHandler<T>;
    onCompositionStart: CompositionEventHandler<T>;
    onCompositionEnd: CompositionEventHandler<T>;
    onPaste: ClipboardEventHandler<T>;
  };
  /** Puts `text` at the caret (replacing a selection), refuses past `max` (nothing changes), refocuses either way. Returns whether it went in. */
  insert(text: string): boolean;
  /** For Send: the text with an emoticon at its end converted (not one just restored by Backspace). */
  finalText(): string;
  /**
   * For a field that keeps its own onChange (PostEditorModal caption): runs the conversion on a
   * change event and returns true when it emitted a converted or restored value; false = not handled.
   */
  tryConvert(e: ChangeEvent<T>): boolean;
  /** Same for compositionend. */
  tryConvertAfterComposition(e: CompositionEvent<T>): boolean;
}

const cpLength = (s: string) => [...s].length;

/**
 * Emoji shortcodes and emoticons in a controlled textarea (spec 2026-11-05 "Conversion rules").
 * `max` counts code points.
 */
export function useEmojiText<T extends Field = HTMLTextAreaElement>(
  value: string,
  onChange: (v: string) => void,
  opts?: { max?: number },
): EmojiText<T> {
  const max = opts?.max;
  const ref = useRef<T | null>(null);
  const valueRef = useRef(value);
  valueRef.current = value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const composing = useRef(false);
  const valueAtCompositionStart = useRef<string | null>(null);
  const undo = useRef<EmojiUndo | null>(null);
  const suppress = useRef<string | null>(null);
  const pendingCaret = useRef<number | null>(null);

  useEffect(() => {
    void ensureEmojiList();
  }, []);

  // The caret is put back once the converted value has rendered.
  useLayoutEffect(() => {
    if (pendingCaret.current === null) return;
    const at = pendingCaret.current;
    pendingCaret.current = null;
    ref.current?.setSelectionRange(at, at);
  });

  const emit = useCallback((v: string) => {
    valueRef.current = v;
    onChangeRef.current(v);
  }, []);

  const keepSuppress = (v: string, caret: number) => {
    if (suppress.current && wordAt(v, caret) !== suppress.current) suppress.current = null;
  };

  const run = useCallback(
    (prev: string, next: string, caret: number, inputType: string, isComposing: boolean): boolean => {
      if (isComposing || inputType === "insertFromPaste" || inputType === "insertFromDrop") {
        undo.current = null;
        // Pasted or dropped text never converts, not even later (next space, Send).
        if (!isComposing) suppress.current = wordAt(next, caret);
        return false;
      }
      if (inputType === "deleteContentBackward" && undo.current) {
        const r = restoreOnBackspace(prev, next, caret, undo.current);
        if (r) {
          suppress.current = undo.current.original.trimEnd();
          undo.current = null;
          pendingCaret.current = r.caret;
          emit(r.value);
          return true;
        }
      }
      const r = convertTyped(prev, next, caret, { max, composing: false, suppress: suppress.current });
      if (r) {
        undo.current = r.undo;
        keepSuppress(r.value, r.caret);
        pendingCaret.current = r.caret;
        emit(r.value);
        return true;
      }
      undo.current = null;
      keepSuppress(next, caret);
      return false;
    },
    [emit, max],
  );

  const tryConvert = useCallback(
    (e: ChangeEvent<T>): boolean => {
      const el = e.target;
      const ne = e.nativeEvent as InputEvent;
      return run(
        valueRef.current,
        el.value,
        el.selectionStart ?? el.value.length,
        ne.inputType ?? "",
        composing.current || !!ne.isComposing,
      );
    },
    [run],
  );

  const tryConvertAfterComposition = useCallback(
    (e: CompositionEvent<T>): boolean => {
      composing.current = false;
      const el = e.currentTarget;
      const v = el.value;
      if (v === valueAtCompositionStart.current) return false; // nothing was composed
      const caret = el.selectionStart ?? v.length;
      const cp = [...v.slice(0, caret)].pop() ?? "";
      const prev = v.slice(0, caret - cp.length) + v.slice(caret);
      return run(prev, v, caret, "", false);
    },
    [run],
  );

  const props: EmojiText<T>["props"] = {
    onChange: (e) => {
      if (!tryConvert(e)) emit(e.target.value);
    },
    onCompositionStart: (e) => {
      valueAtCompositionStart.current = e.currentTarget.value;
      composing.current = true;
    },
    onCompositionEnd: (e) => {
      tryConvertAfterComposition(e);
    },
    onPaste: () => {
      undo.current = null;
    },
  };

  const insert = (text: string): boolean => {
    const el = ref.current;
    const cur = valueRef.current;
    const s = el?.selectionStart ?? cur.length;
    const e = el?.selectionEnd ?? cur.length;
    const next = cur.slice(0, s) + text + cur.slice(e);
    if (max !== undefined && cpLength(next) > max) {
      el?.focus();
      return false;
    }
    undo.current = null;
    pendingCaret.current = s + text.length;
    emit(next);
    el?.focus();
    return true;
  };

  const finalText = () => convertAtEnd(valueRef.current, max, suppress.current);

  return { ref, props, insert, finalText, tryConvert, tryConvertAfterComposition };
}
